import type {
  HLAccountState,
  HLPosition,
  HLTradingBalance,
} from "./types"
import { getHyperliquidEquity } from "./utils/getHyperliquidEquity"
import { toFiniteNumber } from "./utils/number"
import { getPnlPercent } from "./utils/portfolioPnl"

type AggregatedPerpsAccount = {
  positions: HLPosition[]
  equity: number
  available: number
  marginUsed: number
  unrealizedPnl: number
  unrealizedPnlPercent: number
  isEmpty: boolean
}

export function aggregatePerpsAccount({
  tradingBalance,
  additionalAccountStates,
}: {
  tradingBalance: HLTradingBalance
  additionalAccountStates: readonly HLAccountState[]
}): AggregatedPerpsAccount {
  const accountStates = [
    tradingBalance.accountState,
    ...additionalAccountStates,
  ]
  const positions = accountStates.flatMap((state) => state.positions)
  const marginUsed = sumAccountStates(
    accountStates,
    (state) => state.marginSummary.totalMarginUsed
  )
  const unrealizedPnl = positions.reduce(
    (total, position) => total + toFiniteNumber(position.unrealizedPnl),
    0
  )
  const equity =
    tradingBalance.source === "spotUsdc"
      ? getHyperliquidEquity(tradingBalance)
      : sumAccountStates(
          accountStates,
          (state) => state.marginSummary.accountValue
        )
  const available = toFiniteNumber(tradingBalance.available)

  return {
    positions,
    equity,
    available,
    marginUsed,
    unrealizedPnl,
    unrealizedPnlPercent: getPnlPercent(unrealizedPnl, equity),
    isEmpty:
      equity <= 0 &&
      available <= 0 &&
      marginUsed <= 0 &&
      positions.length === 0,
  }
}

function sumAccountStates(
  accountStates: readonly HLAccountState[],
  select: (state: HLAccountState) => string
) {
  return accountStates.reduce(
    (total, state) => total + toFiniteNumber(select(state)),
    0
  )
}
